import { type WallType } from '@cx-sdk/types';
import { type RequestContext } from '@cx-web/domain-types';

import { type ComponentTag } from '~/apps/product-plus/constants/component-tags.ts';

/**
 * Pure function to determine wall type based on user walls and component tags
 * @param user - User context
 * @param tags - Component tags to check against
 * @returns Wall type if user is walled, undefined otherwise
 */
export function getWallType(user: RequestContext['user'], tags: readonly ComponentTag[]): WallType | undefined {
  const walls = user.walls.filter(wall => tags.some(tag => tag === wall.componentTag));
  return walls[0]?.type;
}

/**
 * Pure function to extract coupons from available products
 * @param products - Available products from user context
 * @returns Array of coupons from products that have them
 */
export function getCoupons(products: RequestContext['user']['availableProducts']) {
  return (products || [])
    .filter(product => product && product?.price?.coupons?.length > 0)
    .flatMap(product => product.price.coupons);
}

/**
 * Pure function to get products from user context
 * @param user - User context
 * @returns Array of available products
 */
export function getProducts(user: RequestContext['user']) {
  return user.availableProducts || [];
}

/**
 * Pure function to get a product by ID from available products
 * @param products - Available products from user context
 * @param productId - The product ID to find
 * @returns The product if found, undefined otherwise
 */
export function getProductById(products: RequestContext['user']['availableProducts'], productId: string) {
  return (products || []).find(product => product.id === productId);
}

/**
 * Pure function to get a coupon for a specific product by coupon ID
 * @param products - Available products from user context
 * @param productId - The product ID
 * @param couponId - The coupon ID to find
 * @returns The coupon if found, undefined otherwise
 */
export function getCouponForProduct(
  products: RequestContext['user']['availableProducts'],
  productId: string,
  couponId: string | null | undefined,
) {
  const product = getProductById(products, productId);
  if (!product?.price.coupons.length) return undefined;
  return couponId ? product.price.coupons.find(coupon => coupon.id === couponId) : undefined;
}

/**
 * Pure function to get the default coupon for a product
 * @param products - Available products from user context
 * @param productId - The product ID
 * @returns The default coupon if found, undefined otherwise
 */
export function getDefaultCouponForProduct(products: RequestContext['user']['availableProducts'], productId: string) {
  const product = getProductById(products, productId);
  if (!product?.price.coupons.length) return undefined;
  return product.price.coupons.find(coupon => coupon.isDefault);
}

/**
 * Pure function to check if user has a specific product
 * @param products - Available products from user context
 * @param productId - The product ID to check
 * @returns True if the product exists, false otherwise
 */
export function hasProduct(products: RequestContext['user']['availableProducts'], productId: string): boolean {
  return getProductById(products, productId) !== undefined;
}

/**
 * Pure function to get all product IDs
 * @param products - Available products from user context
 * @returns Array of product IDs
 */
export function getProductIds(products: RequestContext['user']['availableProducts']): string[] {
  return (products || []).map(p => p.id);
}

/**
 * Pure function to check if user has any products
 * @param products - Available products from user context
 * @returns True if user has at least one product
 */
export function hasAnyProduct(products: RequestContext['user']['availableProducts']): boolean {
  return (products || []).length > 0;
}

/**
 * Pure function to check if a feature flag is enabled
 * @param featureFlags - Feature flags from request context
 * @param flagName - The name of the feature flag to check
 * @returns True if the feature flag exists and is enabled, false otherwise
 */
export function hasFeatureFlag(featureFlags: RequestContext['featureFlags'], flagName: string): boolean {
  return featureFlags.find(flag => flag.name === flagName)?.enabled ?? false;
}

/**
 * Pure function to get a feature flag by name
 * @param featureFlags - Feature flags from request context
 * @param flagName - The name of the feature flag to find
 * @returns The feature flag if found, undefined otherwise
 */
export function getFeatureFlag(featureFlags: RequestContext['featureFlags'], flagName: string) {
  return featureFlags.find(flag => flag.name === flagName);
}

/**
 * Pure function to check if user is walled (has any walls)
 * @param user - User context
 * @returns True if user has at least one wall, false otherwise
 */
export function isUserWalled(user: RequestContext['user']): boolean {
  return user.walls.length > 0;
}

/**
 * Pure function to check if user has a specific wall type
 * @param user - User context
 * @param wallType - The wall type to check for
 * @returns True if user has the specified wall type, false otherwise
 */
export function hasWallType(user: RequestContext['user'], wallType: WallType): boolean {
  return user.walls.some(wall => wall.type === wallType);
}
